import React, { useState, useMemo } from 'react';
import {
    Typography, Input, Space, Empty, Checkbox, Tag, Button, Badge, Divider,
} from 'antd';
import {
    SafetyCertificateOutlined, SearchOutlined, CloseOutlined,
    AppstoreOutlined, CheckSquareOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { ACTION_COLORS, ACTION_LABELS } from '../variables/rbaColors';

const { Text } = Typography;

const groupByModule = (habilitations, autres) =>
    habilitations.reduce((acc, h) => {
        const mod = h.moduleNom || h.moduleEntity?.nomModule || autres;
        if (!acc[mod]) acc[mod] = [];
        acc[mod].push(h);
        return acc;
    }, {});

const HabilitationSelector = ({ habilitations, value = [], onChange }) => {
    const { t } = useTranslation('commun');
    const [search, setSearch] = useState('');

    const grouped = useMemo(() => groupByModule(habilitations, t('habilitationSelector.autres')), [habilitations, t]);

    const filtered = useMemo(() => {
        const q = search.toLowerCase();
        if (!q) return grouped;
        return Object.fromEntries(
            Object.entries(grouped)
                .map(([mod, habs]) => [
                    mod,
                    habs.filter(
                        (h) =>
                            h.codeHabilitation?.toLowerCase().includes(q) ||
                            h.libelleHabilitation?.toLowerCase().includes(q) ||
                            mod.toLowerCase().includes(q),
                    ),
                ])
                .filter(([, habs]) => habs.length > 0),
        );
    }, [grouped, search]);

    const selectedSet = useMemo(() => new Set(value), [value]);

    const toggleModule = (moduleName) => {
        const moduleHabs = grouped[moduleName] || [];
        const allSelected = moduleHabs.every((h) => selectedSet.has(h.habilitationTrackingId));
        let newSet = new Set(selectedSet);
        if (allSelected) {
            moduleHabs.forEach((h) => newSet.delete(h.habilitationTrackingId));
        } else {
            moduleHabs.forEach((h) => newSet.add(h.habilitationTrackingId));
        }
        onChange?.(Array.from(newSet));
    };

    const toggleHab = (id) => {
        let newSet = new Set(selectedSet);
        if (newSet.has(id)) {
            newSet.delete(id);
        } else {
            newSet.add(id);
        }
        onChange?.(Array.from(newSet));
    };

    const totalSelected = value.length;

    return (
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden', background: '#fff' }}>
            {/* Header */}
            <div style={{
                background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
                padding: '12px 16px',
                borderBottom: '1px solid #bbf7d0',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap',
            }}>
                <Space>
                    <SafetyCertificateOutlined style={{ color: '#1a6b3c', fontSize: 15 }} />
                    <Text style={{ fontWeight: 700, color: '#1a6b3c', fontSize: 13 }}>
                        {t('habilitationSelector.titre')}
                    </Text>
                    {totalSelected > 0 && (
                        <Badge count={totalSelected} style={{ background: '#1a6b3c', fontSize: 11 }} />
                    )}
                </Space>
                <Input
                    prefix={<SearchOutlined style={{ color: '#9ca3af' }} />}
                    placeholder={t('habilitationSelector.filtrer')}
                    allowClear
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    size="small"
                    style={{ width: 240 }}
                />
            </div>

            {/* Corps scrollable */}
            <div style={{ maxHeight: 440, overflowY: 'auto', padding: '8px 0' }}>
                {Object.keys(filtered).length === 0 ? (
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={t('habilitationSelector.aucune')}
                        style={{ padding: '32px 0' }}
                    />
                ) : (
                    Object.entries(filtered).map(([moduleName, habs]) => {
                        const moduleHabsFull = grouped[moduleName] || [];
                        const selectedInModule = moduleHabsFull.filter((h) =>
                            selectedSet.has(h.habilitationTrackingId),
                        ).length;
                        const allChecked = selectedInModule === moduleHabsFull.length;
                        const indeterminate = selectedInModule > 0 && !allChecked;

                        return (
                            <div key={moduleName} style={{ marginBottom: 4 }}>
                                {/* En-tête module */}
                                <div
                                    style={{
                                        display: 'flex', alignItems: 'center', gap: 10,
                                        padding: '8px 16px',
                                        background: allChecked ? '#f0fdf4' : '#fafafa',
                                        cursor: 'pointer', userSelect: 'none',
                                        borderBottom: '1px solid #f3f4f6',
                                    }}
                                    onClick={() => toggleModule(moduleName)}
                                >
                                    <Checkbox
                                        checked={allChecked}
                                        indeterminate={indeterminate}
                                        onChange={() => toggleModule(moduleName)}
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                    <AppstoreOutlined style={{ color: '#1a6b3c', fontSize: 13 }} />
                                    <Text style={{ fontWeight: 700, fontSize: 13, color: '#1f2937', flex: 1 }}>
                                        {moduleName}
                                    </Text>
                                    <Text type="secondary" style={{ fontSize: 11 }}>
                                        {selectedInModule}/{moduleHabsFull.length}
                                    </Text>
                                </div>

                                {/* Habilitations du module */}
                                <div>
                                    {habs.map((h) => {
                                        const checked = selectedSet.has(h.habilitationTrackingId);
                                        return (
                                            <div
                                                key={h.habilitationTrackingId}
                                                onClick={() => toggleHab(h.habilitationTrackingId)}
                                                style={{
                                                    display: 'flex', alignItems: 'center', gap: 10,
                                                    padding: '7px 16px 7px 40px',
                                                    cursor: 'pointer',
                                                    background: checked ? '#f0fdf4' : 'transparent',
                                                    borderLeft: checked ? '3px solid #1a6b3c' : '3px solid transparent',
                                                    transition: 'background 0.15s, border-color 0.15s',
                                                    userSelect: 'none',
                                                }}
                                                onMouseEnter={(e) => {
                                                    if (!checked) e.currentTarget.style.background = '#f9fafb';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.background = checked ? '#f0fdf4' : 'transparent';
                                                }}
                                            >
                                                <Checkbox
                                                    checked={checked}
                                                    onChange={() => toggleHab(h.habilitationTrackingId)}
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                                <Tag
                                                    color={ACTION_COLORS[h.action]}
                                                    style={{ fontSize: 10, padding: '0 5px', margin: 0, flexShrink: 0 }}
                                                >
                                                    {ACTION_LABELS[h.action] || h.action}
                                                </Tag>
                                                <Text style={{
                                                    fontSize: 12, fontFamily: 'var(--cb-font-mono)',
                                                    color: '#1a6b3c', fontWeight: 700, flexShrink: 0,
                                                }}>
                                                    {h.codeHabilitation}
                                                </Text>
                                                <Text type="secondary" style={{ fontSize: 12 }}>
                                                    {h.libelleHabilitation}
                                                </Text>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {/* Footer résumé */}
            {totalSelected > 0 && (
                <div style={{
                    borderTop: '1px solid #e5e7eb', padding: '10px 16px', background: '#f0fdf4',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 6,
                }}>
                    <Space size={4} wrap>
                        <CheckSquareOutlined style={{ color: '#1a6b3c' }} />
                        <Text style={{ color: '#1a6b3c', fontWeight: 600, fontSize: 12 }}>
                            {t('habilitationSelector.selectionnees', { count: totalSelected })}
                        </Text>
                        <Divider type="vertical" style={{ borderColor: '#86efac', margin: '0 4px' }} />
                        {Object.entries(groupByModule(habilitations.filter((h) => selectedSet.has(h.habilitationTrackingId)), t('habilitationSelector.autres'))).map(([mod, items]) => (
                            <Tag key={mod} color="green" style={{ fontSize: 11, margin: 0 }}>
                                {mod} · {items.length}
                            </Tag>
                        ))}
                    </Space>
                    <Button
                        size="small"
                        icon={<CloseOutlined />}
                        onClick={() => onChange?.([])}
                        style={{ fontSize: 11 }}
                    >
                        {t('habilitationSelector.toutDeselectionner')}
                    </Button>
                </div>
            )}
        </div>
    );
};

export default HabilitationSelector;